import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { useChatContext, ChannelPreviewUIComponentProps } from 'stream-chat-react';
import { ChannelPreviewAvatar } from './ChannelPreviewAvatar.web';
import { UniverseStreamChannel } from '@/services/stream/types';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { formatRelativeDate } from '@/utils/date';

export const ChannelPreviewCustom = (props: ChannelPreviewUIComponentProps) => {
    const { channel, active, lastMessage, unread, setActiveChannel, displayTitle } = props;
    const { channel: activeChannel, client } = useChatContext();
    const router = useRouter();
    
    const isActive = activeChannel?.id === channel.id;

    const onSelect = () => {
        if (setActiveChannel && !isActive) {
            setActiveChannel(channel);
            router.replace(`/?channelId=${channel.id}`);
        }
    };

    const formatTimestamp = (date?: Date) => (date ? formatRelativeDate(date) : '');

    const lastMessageText = lastMessage?.text || 'No messages yet';
    const lastMessageTime = lastMessage?.created_at ? new Date(lastMessage.created_at) : channel.data?.last_message_at ? new Date(channel.data.last_message_at) : undefined;

    // Delivery status checkmark logic
    // If it's the last message from the current user, show checkmark
    const isLastMessageFromMe = lastMessage?.user?.id === client.userID;

    return (
        <TouchableOpacity 
            onPress={onSelect}
            style={[styles.container, isActive && styles.activeContainer]}
        >
            <View style={styles.avatarContainer}>
                <ChannelPreviewAvatar 
                    channel={channel as UniverseStreamChannel} 
                    name={displayTitle} 
                    image={channel.data?.image}
                    size={50}
                />
            </View>
            <View style={styles.contentContainer}>
                <View style={styles.headerRow}>
                    <Text style={[styles.title, isActive && styles.activeText]} numberOfLines={1}>{displayTitle}</Text>
                    <Text style={[styles.timestamp, isActive && styles.activeText]}>{formatTimestamp(lastMessageTime)}</Text>
                </View>
                <View style={styles.messageRow}>
                    <Text style={[styles.lastMessage, isActive && styles.activeText]} numberOfLines={1}>
                        {lastMessageText}
                    </Text>
                    <View style={styles.rightContent}>
                        {lastMessage && isLastMessageFromMe && (
                            <Ionicons name="checkmark-done" size={16} color={isActive ? "#6B7280" : "#9CA3AF"} style={styles.statusIcon} />
                        )}
                        {unread !== undefined && unread > 0 && (
                            <View style={styles.unreadBadge}>
                                <Text style={styles.unreadText}>{unread}</Text>
                            </View>
                        )}
                    </View>
                </View>
            </View>
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        padding: 12,
        backgroundColor: 'white',
        borderBottomWidth: 1,
        borderBottomColor: '#F3F4F6',
        alignItems: 'center',
    },
    activeContainer: {
        backgroundColor: '#E3F4F9',
    },
    activeText: {
        // active state text styles if needed
    },
    avatarContainer: {
        marginRight: 12,
    },
    contentContainer: {
        flex: 1,
        justifyContent: 'center',
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 2,
    },
    title: {
        fontSize: 16,
        fontWeight: '700',
        color: '#111827',
        flex: 1,
    },
    timestamp: {
        fontSize: 12,
        color: '#9CA3AF',
        marginLeft: 8,
    },
    messageRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    lastMessage: {
        fontSize: 14,
        color: '#6B7280',
        flex: 1,
    },
    rightContent: {
        flexDirection: 'row',
        alignItems: 'center',
        marginLeft: 8,
    },
    unreadBadge: {
        backgroundColor: '#EF4444',
        borderRadius: 10,
        minWidth: 20,
        height: 20,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 4,
        marginLeft: 4,
    },
    unreadText: {
        color: 'white',
        fontSize: 11,
        fontWeight: 'bold',
    },
    statusIcon: {
        // marginRight: 4,
    },
});
